import React, { useContext, useEffect, useState } from 'react';
import Delete from '@material-ui/icons/Delete';
import Edit from '@material-ui/icons/Edit';
import ShoppingCart from '@material-ui/icons/ShoppingCart';
import { Button } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import QtySelector from 'components/QtySelector/qtySelector';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import { Price } from 'compositions/OrderDetail/components/PartsAndServices/Price';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGetPrices } from 'hooks/useGetPrices';
import cloneDeep from 'lodash/cloneDeep';
import { AdditionalServicesType, UFOPart } from 'types/UFOTypes';
import { MAXIMUM_MOUNTED_WHEELS, MAXIMUM_TIRES, SERVICE_DEFAULT_QTY, TIRE_DEFAULT_QTY } from 'utils/constantValues';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { getUniqueHash } from 'utils/getUniqueHash';
import { SelectedServicesType, SelectedTire, WizardContext } from '../WizardContext';
import { ComboResume } from './MountingOption/ComboResume';

interface TireInformationProps {
  setPql?: (p: number) => void;
  pqlPrice?: number;
  setQty: (q: number) => void;
  qty: number;
  tireIsSelected?: boolean;
  tire?: UFOPart;
  service?: AdditionalServicesType;
  line?: number;
  selectedServices?: SelectedServicesType[];
  setSelectedServices?: React.Dispatch<React.SetStateAction<SelectedServicesType[]>>;
  setSelectedTires?: React.Dispatch<React.SetStateAction<SelectedTire[]>>;
  selectedTires?: SelectedTire[];
  commonPricesParams: CommonPricesParamsType;
  mobileView?: boolean;
  stockingOrder: boolean;
}
export default function Pricing(props: TireInformationProps) {
  const {
    qty,
    tireIsSelected,
    tire,
    setQty,
    service,
    line,
    setPql,
    selectedServices,
    setSelectedServices,
    setSelectedTires,
    commonPricesParams,
    pqlPrice,
    selectedTires,
    mobileView,
    stockingOrder,
  } = props;
  const isDealer = useCurrentLocationIsDealer();
  const [isSelected, setIsSelected] = useState(false);
  const [isEditingPqlPrice, setIsEditingPqlPrice] = useState(false);
  const { setMountingOptionsModalOpen: setMountingOptionsModalOpenWizard, removePartFromGroup } = useContext(WizardContext);
  const { setMountingOptionsModalOpen: setMountingOptionsModalOpenOrder } = useContext(OrderDetailContext);

  const pqlPricesResponse = useGetPrices(
    service?.ispql
      ? {
          ...commonPricesParams,

          parts: [{ mspn: getMspn(service) as string, qty, is_pql: service?.ispql || false }],
        }
      : null,
  );
  const { t } = useTranslation();

  if (!pqlPrice && setPql && !pqlPricesResponse.isLoading && pqlPricesResponse.data?.pqlParts[0].unitAmout) {
    setPql(pqlPricesResponse.data?.pqlParts[0].unitAmout);
  }

  useEffect(() => {
    if (selectedServices && tireIsSelected === undefined && tireIsSelected !== isSelected) {
      const s = selectedServices.find((p) => p.part.mspn === getMspn(service))?.lines[line || 0];
      if (s !== undefined) setIsSelected(s.selected && !s.removed);
    }
    if (tireIsSelected !== undefined && tireIsSelected !== isSelected) {
      setIsSelected(tireIsSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tireIsSelected, selectedServices, line, service]);

  const maximumTires = stockingOrder ? MAXIMUM_MOUNTED_WHEELS : MAXIMUM_TIRES;

  const maxQty = Number(service?.additional_information?.max_quantity || maximumTires);
  const tiresCount = selectedTires?.reduce((count, st) => (!st.removed ? count + (st.qty || 0) : count), 0) || 0;
  const fixedDefaultValue = maxQty - tiresCount > TIRE_DEFAULT_QTY ? TIRE_DEFAULT_QTY : maxQty - tiresCount;

  const addToTheCart = () => {
    if (tire) {
      if (setSelectedTires) {
        const tireIndex = selectedTires?.findIndex((st) => st.part.mspn === getMspn(tire)) ?? -1;
        if (tireIndex > -1 && selectedTires) {
          // Tire is on the array but unselected
          const editedTire = cloneDeep(selectedTires);
          editedTire[tireIndex].removed = false;
          setSelectedTires(editedTire);
        } else {
          // New tire, never added before
          setSelectedTires((prevState) => [
            ...prevState,
            {
              part: tire,
              qty: fixedDefaultValue,
              removed: false,

              related_to: getUniqueHash(),
            },
          ]);
        }
      }
    } else if (service && selectedServices) {
      const selectedService = selectedServices.findIndex((p) => p.part.mspn === getMspn(service));
      const editedSS = [...selectedServices];
      if (line !== undefined) editedSS[selectedService].lines[line].selected = true;
      if (setSelectedServices) setSelectedServices(editedSS);
    }
  };

  const remove = () => {
    if (tire && setSelectedTires) {
      setSelectedTires((prevState) => prevState.map((p) => {
        const edtitedTire = p;
        if (p.part.mspn === tire.mspn) edtitedTire.removed = true;
        return edtitedTire;
      }));
      if (removePartFromGroup) removePartFromGroup(tire.mspn);
    } else if (service && selectedServices) {
      const selectedService = selectedServices.findIndex((p) => p.part.mspn === getMspn(service));
      const editedSS = [...selectedServices];
      if (line !== undefined) editedSS[selectedService].lines[line].selected = false;
      if (setSelectedServices) setSelectedServices(editedSS);
    }
  };


  let mainDivStyle: React.CSSProperties | undefined = !tire
    ? { display: 'flex', justifyContent: 'right', width: '100%' }
    : undefined;
  if (mobileView && tire) {
    mainDivStyle = {
      display: 'flex',
      width: '100%',
      marginTop: 16,
      justifyContent: stockingOrder ? 'center' : 'space-between',
    };
  } else if (tire) mainDivStyle = { textAlign: 'end' };

  if (tire && mobileView && stockingOrder) {
    mainDivStyle = {
      width: '100%',
      marginTop: 16,
      textAlign: 'end',
    };
  }

  let qtyStyle: React.CSSProperties | undefined = tire
    ? {
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        display: 'flex',
        marginTop: 16,
      }
    : {
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        display: 'flex',
        minWidth: 84,
        marginLeft: 16,
      };
  if (mobileView && tire) qtyStyle = { marginLeft: 16 };

  const priceStyle: React.CSSProperties | undefined = tire
    ? { textAlign: 'right', height: 80 }
    : {
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        display: 'flex',
        textAlign: 'right',
        minWidth: 200,
        height: 100,
      };

  const isPql = service ? service.ispql || false : false;

  return (
    <div style={mainDivStyle}>
      {(!isDealer || isPql) && (
        <div style={priceStyle}>
          <Price
            pqlPrice={pqlPrice}
            wizardColors
            isService={!tire}
            status="wizard"
            qty={qty}
            submitButton
            canEdit
            setPqlByMspn={
              setPql
                ? (mspn, price) => {
                    setPql(price);
                  }
                : undefined
            }
            mspn={service ? (getMspn(service) as string) : (getMspn(tire) as string)}
            commonPricesParams={commonPricesParams}
            is_pql={isPql}
            wizardMode
            setIsEditingPqlPrice={setIsEditingPqlPrice}
          />
        </div>
      )}
      {(!stockingOrder || !tire) && (
        <div style={qtyStyle}>
          <QtySelector
            value={qty}
            onAdd={addToTheCart}
            onRemove={remove}
            maxValue={maxQty}
            defaultValue={tire ? fixedDefaultValue : SERVICE_DEFAULT_QTY}
            onChange={(v) => setQty(v)}
            selected={isSelected}
            tiresCount={tire ? tiresCount : undefined}
            disabled={!!isDealer && service && service.ispql && !pqlPrice}
            readonly={!!isDealer && service && service.ispql && isEditingPqlPrice}
          />
        </div>
      )}
      {tire && stockingOrder && (
        <>
          <Button
            onClick={() => {
              if (setMountingOptionsModalOpenWizard) setMountingOptionsModalOpenWizard(tire);
              if (setMountingOptionsModalOpenOrder) setMountingOptionsModalOpenOrder(tire);
            }}
            style={{
              height: 32,
              width: 160,
              borderRadius: 20,
              marginTop: 8,
            }}
            endIcon={tireIsSelected ? <Edit /> : <ShoppingCart />}
            variant="contained"
            size="small"
            disabled={tiresCount >= maxQty && !tireIsSelected}
          >
            {tireIsSelected && t('Edit options')}
            {!tireIsSelected && t('+ Add to cart')}
          </Button>
          {tireIsSelected && (
            <>
              <Button
                onClick={remove}
                style={{
                  height: 32,
                  width: 160,
                  borderRadius: 20,
                  marginTop: 8,
                  whiteSpace: 'nowrap',
                }}
                endIcon={<Delete />}
                variant="contained"
                color="secondary"
                size="small"
              >
                {t('Remove Tire')}
              </Button>
              <ComboResume mspn={tire.mspn} />
            </>
          )}
        </>
      )}
    </div>
  );
}
